// Permission types for different actions
export type Permission =
	| "view:reviews"
	| "create:review"
	| "edit:review"
	| "delete:review"
	| "view:forms"
	| "create:form"
	| "edit:form"
	| "delete:form"
	| "view:import"
	| "import:reviews"
	| "view:branding"
	| "edit:branding"
	| "view:display"
	| "edit:display"
	| "view:teams"
	| "invite:member"
	| "edit:member"
	| "delete:member"
	| "view:billing"
	| "edit:billing"
	| "view:settings"
	| "edit:settings"
	| "delete:workspace"
	| "view:support"
	| "view:roadmap";

export type UserRole = "admin" | "editor";

// Role hierarchy (for role comparison)
export const ROLE_HIERARCHY: Record<UserRole, number> = {
	admin: 2,
	editor: 1,
};

// Permission mapping for each role
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
	admin: [
		// Full access to everything (previously owner permissions)
		"view:reviews",
		"create:review",
		"edit:review",
		"delete:review",
		"view:forms",
		"create:form",
		"edit:form",
		"delete:form",
		"view:import",
		"import:reviews",
		"view:branding",
		"edit:branding",
		"view:display",
		"edit:display",
		"view:teams",
		"invite:member",
		"edit:member",
		"delete:member",
		"view:billing",
		"edit:billing",
		"view:settings",
		"edit:settings",
		"delete:workspace",
		"view:support",
		"view:roadmap",
	],
	editor: [
		// Can view and manage content, but not team or settings
		"view:reviews",
		"create:review",
		"edit:review",
		"view:forms",
		"create:form",
		"edit:form",
		"view:import",
		"import:reviews",
		"view:branding",
		"view:display",
		"view:teams", // Can view team members
		"view:billing", // Can view billing info
		"view:settings", // Can view settings
		"view:support",
		"view:roadmap",
	],
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole | null | undefined, permission: Permission): boolean {
	if (!role) return false;
	return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Check if a role has any of the specified permissions
 */
export function hasAnyPermission(role: UserRole | null | undefined, permissions: Permission[]): boolean {
	if (!role) return false;
	return permissions.some((permission) => hasPermission(role, permission));
}

/**
 * Check if a role has all of the specified permissions
 */
export function hasAllPermissions(role: UserRole | null | undefined, permissions: Permission[]): boolean {
	if (!role) return false;
	return permissions.every((permission) => hasPermission(role, permission));
}

/**
 * Check if a role is equal to or higher than the required role in hierarchy
 */
export function hasMinimumRole(userRole: UserRole | null | undefined, requiredRole: UserRole): boolean {
	if (!userRole) return false;
	return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: UserRole | null | undefined): Permission[] {
	if (!role) return [];
	return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Check if user can perform action on resource (with ownership check)
 */
export function canPerformAction(userRole: UserRole | null | undefined, permission: Permission, isOwner?: boolean): boolean {
	// If user is owner of the resource, they have all permissions
	if (isOwner) return true;

	return hasPermission(userRole, permission);
}
